import React, { useMemo, useState } from 'react';
import './AdminTemplate.css';

const TEMPLATE_API_URL = 'https://inscriptions.ices.fr/backend/api/templates/inscription';
const STORAGE_TOKEN_KEY = 'templateAdminToken';
const MESSAGE_COLOR_OPTIONS = [
  { value: '#ef7c7a', label: 'Rouge' },
  { value: '#efbd4d', label: 'Orange' },
  { value: '#6fb88b', label: 'Vert' }
];
const OPTIONS_HELP = [
  '0 - Indicatifs téléphoniques (pays + code)',
  '1 - Nationalités (démonyms)',
  '2 - Pays (libellé + code ISO numérique)',
  '3 - Catégories socio-professionnelles (CSP/PCS)',
  '4 - Situation maritale',
  '5 - Échelon de bourse (0 bis à 7)',
  '6 - Spécialités du bac (enseignements de spécialité)',
  '7 - Mention au bac',
  '8 - Civilité',
  '9 - Oui (choix unique)',
  '10 - Oui / Non',
  '11 - Modalités de paiement',
  '12 - Domiciliation fiscale des revenus (Français / Étranger)',
  '13 - Origine scolaire / établissement précédent (codification SISE)',
  '14 - Série ou type de baccalauréat (codification)',
  '15 - Type de diplôme obtenu (codification)',
  '16 - Pays (liste simple)'
];

function collectFilterOptions(template) {
  const options = [];
  if (!template || !Array.isArray(template.criteres)) return options;

  const register = (filtres) => {
    if (!Array.isArray(filtres)) return;
    filtres.forEach((group, index) => {
      if (!Array.isArray(group)) return;
      if (!options[index]) options[index] = new Set();
      group.forEach((value) => {
        if (value === null || value === undefined) return;
        const text = String(value).trim();
        if (!text) return;
        options[index].add(text);
      });
    });
  };

  template.criteres.forEach((critere) => {
    register(critere.filtres);
    (critere.sousCriteres || []).forEach((sous) => {
      register(sous.filtres);
      (sous.questions || []).forEach((question) => {
        register(question.filtres);
      });
    });
  });

  return options.map((set) => Array.from(set || []).sort((a, b) => a.localeCompare(b, 'fr')));
}

function parseMaybeNumber(value) {
  if (value === null || value === undefined) return value;
  if (typeof value === 'number') return value;
  const text = String(value).trim();
  if (!text) return '';
  if (/^-?\d+(\.\d+)?$/.test(text)) {
    const num = Number(text);
    return Number.isNaN(num) ? value : num;
  }
  return value;
}

function formatFilterGroup(group) {
  if (!Array.isArray(group) || group.length === 0) return 'Tous';
  return group.join(' / ');
}

function createTemplateMessage(section, index) {
  return {
    id: `${section}_${Date.now()}_${index + 1}`,
    text: '',
    backgroundColor: '#efbd4d'
  };
}

function hexToRgb(hex) {
  const normalized = String(hex || '').trim().toLowerCase();
  const match = normalized.match(/^#([0-9a-f]{6})$/i);
  if (!match) return null;
  const raw = match[1];
  return {
    r: parseInt(raw.slice(0, 2), 16),
    g: parseInt(raw.slice(2, 4), 16),
    b: parseInt(raw.slice(4, 6), 16)
  };
}

function nearestAllowedMessageColor(value) {
  const allowed = MESSAGE_COLOR_OPTIONS.map((option) => option.value);
  const normalized = String(value || '').trim().toLowerCase();
  if (allowed.includes(normalized)) {
    return normalized;
  }

  const sourceRgb = hexToRgb(normalized);
  if (!sourceRgb) return '#efbd4d';

  let nearest = '#efbd4d';
  let minDistance = Number.POSITIVE_INFINITY;

  allowed.forEach((color) => {
    const rgb = hexToRgb(color);
    if (!rgb) return;
    const distance = Math.sqrt(
      ((rgb.r - sourceRgb.r) ** 2) +
      ((rgb.g - sourceRgb.g) ** 2) +
      ((rgb.b - sourceRgb.b) ** 2)
    );
    if (distance < minDistance) {
      minDistance = distance;
      nearest = color;
    }
  });

  return nearest;
}

function AdminTemplate() {
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_TOKEN_KEY) || '');
  const [template, setTemplate] = useState(null);
  const [status, setStatus] = useState({ type: 'idle', message: '' });
  const [dirty, setDirty] = useState(false);
  const [openFilterKey, setOpenFilterKey] = useState(null);
  const [messagePanels, setMessagePanels] = useState({ intro: false, final: false });
  const [openMessageColorKey, setOpenMessageColorKey] = useState(null);

  const filterOptions = useMemo(() => collectFilterOptions(template), [template]);

  const questionTypes = useMemo(() => {
    const types = new Set(['text', 'select', 'search', 'date', 'file', 'autre']);
    if (template && Array.isArray(template.criteres)) {
      template.criteres.forEach((critere) => {
        (critere.sousCriteres || []).forEach((sous) => {
          (sous.questions || []).forEach((question) => {
            if (question?.type && question.type !== 'paiement') {
              types.add(question.type);
            }
          });
        });
      });
    }
    types.delete('paiement');
    return Array.from(types).sort();
  }, [template]);

  const loadTemplate = async () => {
    setStatus({ type: 'loading', message: 'Chargement du template…' });
    try {
      const res = await fetch(TEMPLATE_API_URL, {
        headers: token ? { 'x-admin-token': token } : {}
      });
      const contentType = res.headers.get('content-type') || '';
      if (!res.ok) {
        if (contentType.includes('application/json')) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.message || 'Accès refusé.');
        }
        const text = await res.text();
        throw new Error(text || 'Accès refusé.');
      }
      if (!contentType.includes('application/json')) {
        const text = await res.text();
        throw new Error(text || 'Réponse inattendue du serveur.');
      }
      const data = await res.json();
      setTemplate(data.template || null);
      setDirty(false);
      setStatus({ type: 'success', message: 'Template chargé.' });
    } catch (error) {
      setStatus({ type: 'error', message: error.message || 'Impossible de charger le template.' });
    }
  };

  const saveTemplate = async () => {
    if (!template) return;
    setStatus({ type: 'loading', message: 'Sauvegarde en cours…' });
    try {
      const res = await fetch(TEMPLATE_API_URL, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'x-admin-token': token } : {})
        },
        body: JSON.stringify({ template })
      });
      const contentType = res.headers.get('content-type') || '';
      if (!res.ok) {
        if (contentType.includes('application/json')) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.message || 'Accès refusé.');
        }
        const text = await res.text();
        throw new Error(text || 'Accès refusé.');
      }
      if (!contentType.includes('application/json')) {
        const text = await res.text();
        throw new Error(text || 'Réponse inattendue du serveur.');
      }
      const data = await res.json();
      setTemplate(data.template || template);
      setDirty(false);
      setStatus({ type: 'success', message: 'Template sauvegardé.' });
    } catch (error) {
      setStatus({ type: 'error', message: error.message || 'Erreur de sauvegarde.' });
    }
  };

  const updateTemplate = (updater) => {
    setTemplate((prev) => {
      if (!prev) return prev;
      const next = updater(prev);
      return next;
    });
    setDirty(true);
  };

  const updateCritereName = (critereId, value) => {
    updateTemplate((prev) => ({
      ...prev,
      criteres: (prev.criteres || []).map((critere) =>
        critere.id === critereId ? { ...critere, nom: value } : critere
      )
    }));
  };

  const updateSousCritereName = (critereId, sousCritereId, value) => {
    updateTemplate((prev) => ({
      ...prev,
      criteres: (prev.criteres || []).map((critere) => {
        if (critere.id !== critereId) return critere;
        return {
          ...critere,
          sousCriteres: (critere.sousCriteres || []).map((sous) =>
            sous.id === sousCritereId ? { ...sous, nom: value } : sous
          )
        };
      })
    }));
  };

  const updateQuestionField = (critereId, sousCritereId, questionId, field, value) => {
    updateTemplate((prev) => ({
      ...prev,
      criteres: (prev.criteres || []).map((critere) => {
        if (critere.id !== critereId) return critere;
        return {
          ...critere,
          sousCriteres: (critere.sousCriteres || []).map((sous) => {
            if (sous.id !== sousCritereId) return sous;
            return {
              ...sous,
              questions: (sous.questions || []).map((question) =>
                question.id === questionId ? { ...question, [field]: value } : question
              )
            };
          })
        };
      })
    }));
  };

  const updateMessageSection = (section, updater) => {
    updateTemplate((prev) => {
      const currentMessages = Array.isArray(prev?.messages?.[section]) ? prev.messages[section] : [];
      const nextMessages = updater(currentMessages);
      return {
        ...prev,
        messages: {
          ...(prev.messages || {}),
          [section]: nextMessages
        }
      };
    });
  };

  const addMessage = (section) => {
    updateMessageSection(section, (current) => [...current, createTemplateMessage(section, current.length)]);
  };

  const deleteMessage = (section, id) => {
    updateMessageSection(section, (current) => current.filter((message) => message.id !== id));
  };

  const updateMessageField = (section, id, field, value) => {
    updateMessageSection(section, (current) =>
      current.map((message) => (message.id === id ? { ...message, [field]: value } : message))
    );
  };

  const moveMessage = (section, fromIndex, toIndex) => {
    updateMessageSection(section, (current) => {
      if (toIndex < 0 || toIndex >= current.length) return current;
      const next = [...current];
      const [moved] = next.splice(fromIndex, 1);
      if (!moved) return current;
      next.splice(toIndex, 0, moved);
      return next;
    });
  };

  const toggleMessagePanel = (section) => {
    setMessagePanels((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const renderMessageEditor = (section, title, description) => {
    const messages = Array.isArray(template?.messages?.[section]) ? template.messages[section] : [];
    const isOpen = messagePanels[section] === true;
    return (
      <section className={`template-messages-card${isOpen ? '' : ' template-messages-card--collapsed'}`}>
        <div className="template-messages-card__header">
          <h2>{title}</h2>
          <div className="template-messages-card__header-actions">
            <button type="button" className="btn" onClick={() => toggleMessagePanel(section)}>
              {isOpen ? 'Réduire' : 'Agrandir'}
            </button>
            {isOpen ? (
              <button type="button" className="btn" onClick={() => addMessage(section)}>
                Ajouter un message
              </button>
            ) : null}
          </div>
        </div>

        {isOpen ? (
          <>
            <p className="template-messages-card__description">{description}</p>
            {messages.length === 0 ? (
              <div className="template-messages-card__empty">Aucun message configuré.</div>
            ) : (
              <div className="template-messages-list">
                {messages.map((message, index) => (
                  <article key={message.id || `${section}_${index}`} className="template-message-item">
                    <div className="template-message-item__top">
                      <span>Message #{index + 1}</span>
                      <div className="template-message-item__actions">
                        <button
                          type="button"
                          className="template-message-action"
                          onClick={() => moveMessage(section, index, index - 1)}
                          disabled={index === 0}
                        >
                          Monter
                        </button>
                        <button
                          type="button"
                          className="template-message-action"
                          onClick={() => moveMessage(section, index, index + 1)}
                          disabled={index >= messages.length - 1}
                        >
                          Descendre
                        </button>
                        <button type="button" className="filter-remove" onClick={() => deleteMessage(section, message.id)}>
                          Supprimer
                        </button>
                      </div>
                    </div>

                    <div className="template-message-item__fields">
                      <label className="question-card__field">
                        <span>Couleur de fond</span>
                        {(() => {
                          const selectedColor = nearestAllowedMessageColor(message.backgroundColor || '#efbd4d');
                          const dropdownKey = `${section}:${message.id || index}`;
                          const isOpenDropdown = openMessageColorKey === dropdownKey;
                          return (
                            <div className="template-message-color-dropdown">
                              <button
                                type="button"
                                className="template-message-color-trigger"
                                onClick={() => setOpenMessageColorKey(isOpenDropdown ? null : dropdownKey)}
                                aria-expanded={isOpenDropdown}
                                aria-label="Choisir une couleur"
                                title="Choisir une couleur"
                              >
                                <span
                                  className="template-message-color-swatch template-message-color-swatch--selected"
                                  style={{ backgroundColor: selectedColor }}
                                />
                                <span className="template-message-color-caret">▾</span>
                              </button>

                              {isOpenDropdown ? (
                                <div className="template-message-color-menu" role="listbox">
                                  {MESSAGE_COLOR_OPTIONS.map((option) => (
                                    <button
                                      key={option.value}
                                      type="button"
                                      className={`template-message-color-choice${selectedColor === option.value ? ' is-active' : ''}`}
                                      onClick={() => {
                                        updateMessageField(section, message.id, 'backgroundColor', option.value);
                                        setOpenMessageColorKey(null);
                                      }}
                                      aria-label={option.label}
                                      title={option.label}
                                    >
                                      <span
                                        className="template-message-color-swatch"
                                        style={{ backgroundColor: option.value }}
                                      />
                                    </button>
                                  ))}
                                </div>
                              ) : null}
                            </div>
                          );
                        })()}
                      </label>

                      <label className="question-card__field question-card__field--full">
                        <span>Texte</span>
                        <textarea
                          rows={4}
                          value={message.text || ''}
                          onChange={(event) => updateMessageField(section, message.id, 'text', event.target.value)}
                        />
                      </label>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </>
        ) : null}
      </section>
    );
  };

  const handleTokenChange = (event) => {
    const value = event.target.value;
    setToken(value);
    localStorage.setItem(STORAGE_TOKEN_KEY, value);
  };

  const updateFilters = (level, critereId, sousCritereId, questionId, nextFiltres) => {
    updateTemplate((prev) => ({
      ...prev,
      criteres: (prev.criteres || []).map((critere) => {
        if (critere.id !== critereId) return critere;
        if (level === 'critere') {
          return { ...critere, filtres: nextFiltres };
        }
        return {
          ...critere,
          sousCriteres: (critere.sousCriteres || []).map((sous) => {
            if (sous.id !== sousCritereId) return sous;
            if (level === 'sous') {
              return { ...sous, filtres: nextFiltres };
            }
            return {
              ...sous,
              questions: (sous.questions || []).map((question) =>
                question.id === questionId ? { ...question, filtres: nextFiltres } : question
              )
            };
          })
        };
      })
    }));
  };

  const toggleFilterValue = (current, index, value) => {
    const next = Array.isArray(current) ? current.map((group) => [...(group || [])]) : [];
    const group = next[index] || [];
    const exists = group.includes(value);
    const updated = exists ? group.filter((entry) => entry !== value) : [...group, value];
    next[index] = updated;
    return next;
  };

  const renderFiltresEditor = (filtres, level, critereId, sousCritereId, questionId) => {
    if (!Array.isArray(filterOptions) || filterOptions.length === 0) return null;
    const current = Array.isArray(filtres) ? filtres : [];
    const hasConfiguredFilters = current.some((group) => Array.isArray(group) && group.length > 0);
    const allCheckedFilters = filterOptions.map((options) => [...options]);
    const emptyFilters = filterOptions.map(() => []);

    if (!hasConfiguredFilters) {
      return (
        <div className="template-filters">
          <div className="template-filters__title">Filtres</div>
          <button
            type="button"
            className="filter-add"
            onClick={() => updateFilters(level, critereId, sousCritereId, questionId, allCheckedFilters)}
          >
            Ajouter des filtres
          </button>
        </div>
      );
    }

    return (
      <div className="template-filters">
        <div className="template-filters__header">
          <div className="template-filters__title">Filtres</div>
          <button
            type="button"
            className="filter-remove"
            onClick={() => {
              const confirmed = window.confirm('Voulez-vous vraiment supprimer tous les filtres ?');
              if (!confirmed) return;
              updateFilters(level, critereId, sousCritereId, questionId, emptyFilters);
            }}
          >
            Supprimer les filtres
          </button>
        </div>
        <div className="template-filters-row">
          {filterOptions.map((options, index) => {
            const selected = current[index] || [];
            const label = selected.length ? formatFilterGroup(selected) : 'Tous';
            const key = `${level}:${critereId || ''}:${sousCritereId || ''}:${questionId || ''}:${index}`;
            const isOpen = openFilterKey === key;
            return (
              <div key={key} className={`filter-pill-wrapper${isOpen ? ' filter-pill-wrapper--open' : ''}`}>
                <button
                  type="button"
                  className={`filter-pill ${selected.length ? '' : 'filter-pill--empty'}`}
                  onClick={() => setOpenFilterKey(isOpen ? null : key)}
                >
                  Filtre {index + 1} : {label}
                </button>
                {isOpen ? (
                  <div className="filter-dropdown">
                    {options.map((option) => {
                      const checked = selected.includes(option);
                      return (
                        <label key={option} className="filter-dropdown__option">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => {
                              const next = toggleFilterValue(current, index, option);
                              updateFilters(level, critereId, sousCritereId, questionId, next);
                            }}
                          />
                          <span>{option}</span>
                        </label>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="template-admin">
      <header className="template-admin__header">
        <div>
          <p className="template-admin__eyebrow">Administration</p>
          <h1>Template Inscription</h1>
          <a href="/admin/mails">E-mails automatiques</a>
          <p className="template-admin__subtitle">
            Seuls les champs Étiquette, QuestionHelp, Filtres et Obligatoire sont modifiables.
          </p>
        </div>
        <div className="template-admin__summary">
          <div>
            <span>Template</span>
            <strong>{template?.name || 'Inscription'}</strong>
          </div>
          <div>
            <span>Critères</span>
            <strong>{template?.criteres?.length || 0}</strong>
          </div>
        </div>
      </header>

      <section className="template-admin__toolbar">
        <label className="template-admin__field">
          <span>Mot de passe</span>
          <input
            type="password"
            placeholder="ADMIN_TEMPLATE_TOKEN"
            value={token}
            onChange={handleTokenChange}
          />
        </label>
        <div className="template-admin__actions">
          <button type="button" className="btn" onClick={loadTemplate}>
            Charger
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={saveTemplate}
            disabled={!template || !dirty}
          >
            Sauvegarder
          </button>
        </div>
      </section>

      {status.message ? (
        <div className={`template-admin__status template-admin__status--${status.type}`}>{status.message}</div>
      ) : null}

      {!template ? (
        <section className="template-admin__empty">
          <p>Renseignez le mot de passe puis cliquez sur Charger pour accéder au template.</p>
        </section>
      ) : (
        <main className="template-admin__content">
          <datalist id="template-type-options">
            {questionTypes.map((type) => (
              <option key={type} value={type} />
            ))}
          </datalist>

          {renderMessageEditor(
            'intro',
            'Étape avant sélection de filière',
            'Ces messages s’affichent avant l’accès au dossier.'
          )}
          {renderMessageEditor(
            'final',
            'Messages de fin de dossier',
            'Ces messages s’affichent quand le dossier est soumis.'
          )}

          {(template.criteres || []).map((critere, critereIndex) => (
            <section
              key={critere.id}
              className="critere-card"
              style={{ '--delay': `${critereIndex * 0.04}s` }}
            >
              <div className="critere-card__header">
                <div>
                  <p className="critere-card__eyebrow">Critère #{critere.id}</p>
                  <input
                    className="critere-card__input"
                    value={critere.nom || ''}
                    readOnly
                    onChange={(event) => updateCritereName(critere.id, event.target.value)}
                  />
                </div>
                <div className="critere-card__meta">
                  {critere.sousCriteres?.length || 0} sous-critères
                </div>
              </div>

              {renderFiltresEditor(critere.filtres, 'critere', critere.id)}

              <div className="sous-critere-list">
                {(critere.sousCriteres || []).map((sous, sousIndex) => (
                  <div
                    key={sous.id}
                    className="sous-critere-card"
                    style={{ '--delay': `${(critereIndex + sousIndex + 1) * 0.03}s` }}
                  >
                    <div className="sous-critere-card__header">
                      <div>
                        <p className="sous-critere-card__eyebrow">Sous-critère #{sous.id}</p>
                        <input
                          className="sous-critere-card__input"
                          value={sous.nom || ''}
                          readOnly
                          onChange={(event) =>
                            updateSousCritereName(critere.id, sous.id, event.target.value)
                          }
                        />
                      </div>
                      <div className="sous-critere-card__meta">
                        {sous.questions?.length || 0} questions
                      </div>
                    </div>

                    {renderFiltresEditor(sous.filtres, 'sous', critere.id, sous.id)}

                    <div className="question-grid">
                      {(sous.questions || [])
                        .filter((question) => question?.type !== 'paiement' && !question?.paiement)
                        .map((question, questionIndex) => {
                          const questionFilterPrefix = `question:${critere.id}:${sous.id}:${question.id}:`;
                          const isQuestionFilterOpen =
                            typeof openFilterKey === 'string' &&
                            openFilterKey.startsWith(questionFilterPrefix);

                          return (
                            <article
                              key={question.id}
                              className={`question-card${isQuestionFilterOpen ? ' question-card--filters-open' : ''}`}
                              style={{ '--delay': `${(questionIndex + 1) * 0.02}s` }}
                            >
                            <div className="question-card__header">
                              <div>
                                <p className="question-card__eyebrow">Question #{question.id}</p>
                                <input
                                  className="question-card__title"
                                  value={question.nom || ''}
                                  readOnly
                                  onChange={(event) =>
                                    updateQuestionField(
                                      critere.id,
                                      sous.id,
                                      question.id,
                                      'nom',
                                      event.target.value
                                    )
                                  }
                                />
                              </div>
                              <label className="question-card__field">
                                <span>Type</span>
                                <input
                                  list="template-type-options"
                                  value={question.type || ''}
                                  readOnly
                                  onChange={(event) =>
                                    updateQuestionField(
                                      critere.id,
                                      sous.id,
                                      question.id,
                                      'type',
                                      event.target.value
                                    )
                                  }
                                />
                              </label>
                            </div>

                            <div className="question-card__grid">
                              <label className="question-card__field question-card__field--toggle">
                                <span>Obligatoire</span>
                                <input
                                  type="checkbox"
                                  checked={question.required === 'required'}
                                  onChange={(event) =>
                                    updateQuestionField(
                                      critere.id,
                                      sous.id,
                                      question.id,
                                      'required',
                                      event.target.checked ? 'required' : ''
                                    )
                                  }
                                />
                              </label>
                              <label className="question-card__field">
                                <span>Étiquette</span>
                                <input
                                  value={question.etiquette || ''}
                                  onChange={(event) =>
                                    updateQuestionField(
                                      critere.id,
                                      sous.id,
                                      question.id,
                                      'etiquette',
                                      event.target.value
                                    )
                                  }
                                />
                              </label>
                              <label className="question-card__field">
                                <span>idForm</span>
                                <input
                                  value={question.idForm || ''}
                                  readOnly
                                  onChange={(event) =>
                                    updateQuestionField(
                                      critere.id,
                                      sous.id,
                                      question.id,
                                      'idForm',
                                      event.target.value
                                    )
                                  }
                                />
                              </label>
                              {question.type === 'search' ? (
                                <label className="question-card__field">
                                  <span>idFormCode</span>
                                  <input
                                    value={question.idFormCode || ''}
                                    readOnly
                                    onChange={(event) =>
                                      updateQuestionField(
                                        critere.id,
                                        sous.id,
                                        question.id,
                                        'idFormCode',
                                        event.target.value
                                      )
                                    }
                                  />
                                </label>
                              ) : null}
                              {question.type === 'text' ? (
                                <label className="question-card__field">
                                  <span>Placeholder</span>
                                  <input
                                    value={question.placeholder ?? ''}
                                    readOnly
                                    onChange={(event) =>
                                      updateQuestionField(
                                        critere.id,
                                        sous.id,
                                        question.id,
                                        'placeholder',
                                        parseMaybeNumber(event.target.value)
                                      )
                                    }
                                  />
                                </label>
                              ) : null}
                              {question.type === 'select' ? (
                                <label className="question-card__field">
                                  <span className="field-with-info">
                                    Options
                                    <span className="info-bubble" role="note" aria-label="Aide sur les options" tabIndex={0}>
                                      i
                                      <span className="info-bubble__content">
                                        {OPTIONS_HELP.map((line) => (
                                          <span key={line} className="info-bubble__line">
                                            {line}
                                          </span>
                                        ))}
                                      </span>
                                    </span>
                                  </span>
                                  <input
                                    type="number"
                                    value={question.Options ?? ''}
                                    readOnly
                                    onChange={(event) =>
                                      updateQuestionField(
                                        critere.id,
                                        sous.id,
                                        question.id,
                                        'Options',
                                        parseMaybeNumber(event.target.value)
                                      )
                                    }
                                  />
                                </label>
                              ) : null}
                            </div>

                            <label className="question-card__field question-card__field--full">
                              <span>QuestionHelp</span>
                              <textarea
                                rows={3}
                                value={question.QuestionHelp || ''}
                                onChange={(event) =>
                                  updateQuestionField(
                                    critere.id,
                                    sous.id,
                                    question.id,
                                    'QuestionHelp',
                                    event.target.value
                                  )
                                }
                              />
                            </label>

                            {renderFiltresEditor(question.filtres, 'question', critere.id, sous.id, question.id)}
                            </article>
                          );
                        })}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </main>
      )}
    </div>
  );
}

export default AdminTemplate;
